// Template do Contrato de Projeto e Instalação Fotovoltaico (HangSix).
// O HTML é renderizado em PDF (html2canvas + jsPDF) mantendo o visual do modelo.
// Os dados de cliente, kit solar e empresa (CONTRATADA) são substituídos dinamicamente.

// Logo padrão (HangSix) usada quando a empresa do cliente não possui logo_url cadastrada.
// Arquivo servido a partir de public/ (mesma origem, compatível com html2canvas).
export const LOGO_HANGSIX_PADRAO = '/HangSixLogo.png'

const escapeHtml = (value) => {
  if (value == null) return ''
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Monta o HTML completo do contrato com os dados substituídos.
 * @param {Object} dados
 * @param {string} dados.empresaNome           Nome de exibição da CONTRATADA
 * @param {string} dados.empresaRazaoSocial    Razão social da CONTRATADA
 * @param {string} dados.empresaCnpj
 * @param {string} dados.empresaEndereco
 * @param {string} dados.empresaEmail
 * @param {string} dados.empresaTelefone
 * @param {string} dados.empresaLogo           data URI ou URL do logo
 * @param {string} dados.clienteNome           Nome do CONTRATANTE
 * @param {string} dados.clienteCpf            CPF/CNPJ formatado
 * @param {string} dados.kitSolarHtml          HTML do kit (linhas inversor/módulo) ou texto "não cadastrado"
 * @param {string} dados.valorTexto            Texto do valor do projeto
 * @param {string} [dados.multaTexto]          Texto da multa de cancelamento (ex.: "50% ... correspondente a R$ X")
 * @returns {string} HTML
 */
export function buildContratoHtml(dados) {
  const empresaNome = escapeHtml(dados.empresaNome || 'HangSix Energia Solar')
  const empresaRazao = escapeHtml(dados.empresaRazaoSocial || dados.empresaNome || 'HangSix Energia Solar')
  const empresaCnpj = escapeHtml(dados.empresaCnpj || '44.376.302/0001-34')
  const empresaEndereco = escapeHtml(dados.empresaEndereco || 'Tv. WE29, Nº 2, Cidade Nova IV, CEP: 67133-120, Ananindeua - PA')
  const empresaEmail = escapeHtml(dados.empresaEmail || 'hangsixsolar@gmail.com')
  const empresaTelefone = escapeHtml(dados.empresaTelefone || '(91) 9 9253-3339')
  // Logo: usa a logo informada. Quando não houver e `semLogo` for true, o contrato
  // é gerado sem nenhuma logo (sistema com várias empresas). Caso contrário, mantém
  // a logo padrão HangSix por compatibilidade.
  const logoSrc = dados.empresaLogo || (dados.semLogo ? '' : LOGO_HANGSIX_PADRAO)
  // Cores do contrato (personalizáveis por empresa). Os defaults mantêm o visual original.
  const corPrimaria = dados.corPrimaria || '#1a202c'       // títulos escuros / fundo das seções
  const corDestaque = dados.corDestaque || '#f58220'       // detalhes / accent
  const corDestaqueEscura = dados.corDestaque || '#dd6b20' // alertas / highlight
  const corTextoAlerta = dados.corPrimaria || '#7b341e'    // texto do box de alerta
  const clienteNome = escapeHtml(dados.clienteNome || 'Nome não informado')
  const clienteCpf = escapeHtml(dados.clienteCpf || 'N/A')
  const kitSolarHtml = dados.kitSolarHtml || 'Kit solar não cadastrado para este cliente no momento da emissão deste instrumento.'
  const valorTexto = escapeHtml(dados.valorTexto || 'O valor total do projeto não foi informado no cadastro do sistema.')
  const multaTexto = escapeHtml(
    dados.multaTexto ||
    '50% (cinquenta por cento) do valor total do projeto'
  )

  return `
<div class="contrato-root">
  <style>
    .contrato-root * { box-sizing: border-box; }
    .contrato-root {
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
      font-size: 10.5pt;
      line-height: 1.6;
      color: #1a202c;
      background-color: #ffffff;
      padding: 0;
    }
    .contrato-root .header { text-align: center; margin-bottom: 25px; border-bottom: 3px solid ${corDestaque}; padding-bottom: 15px; }
    .contrato-root .logo { max-width: 220px; height: auto; margin-bottom: 10px; }
    .contrato-root h1 { font-size: 16pt; color: ${corPrimaria}; text-transform: uppercase; margin: 10px 0 5px 0; letter-spacing: 0.5px; font-weight: bold; }
    .contrato-root h2 { font-size: 12pt; color: ${corDestaque}; text-transform: uppercase; margin-top: 0; margin-bottom: 15px; font-weight: 600; }
    .contrato-root .section { margin-bottom: 20px; }
    .contrato-root .section-title {
      font-size: 11pt; font-weight: bold; color: #ffffff;
      background-color: ${corPrimaria};
      background-image: linear-gradient(${corPrimaria}, ${corPrimaria});
      padding: 6px 10px; margin-bottom: 10px; text-transform: uppercase;
      border-left: 5px solid ${corDestaque};
      -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact;
      page-break-after: avoid; break-after: avoid;
      page-break-inside: avoid; break-inside: avoid;
    }
    .contrato-root p { margin: 0 0 10px 0; text-align: justify; orphans: 3; widows: 3; }
    .contrato-root .parties-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; page-break-inside: avoid; break-inside: avoid; }
    .contrato-root .parties-table td { padding: 6px 8px; vertical-align: top; border: 1px solid #e2e8f0; }
    .contrato-root .parties-table td.label {
      font-weight: bold; background-color: #f7fafc; background-image: linear-gradient(#f7fafc, #f7fafc);
      width: 20%; color: #4a5568;
      -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact;
    }
    .contrato-root ul, .contrato-root ol { margin: 0 0 10px 0; padding-left: 20px; }
    .contrato-root li { margin-bottom: 6px; text-align: justify; orphans: 3; widows: 3; }
    .contrato-root .alert-box {
      background-color: #fffaf0; background-image: linear-gradient(#fffaf0, #fffaf0);
      border-left: 4px solid ${corDestaqueEscura}; padding: 10px 12px; margin-bottom: 15px;
      font-style: italic; color: ${corTextoAlerta};
      -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact;
      page-break-inside: avoid; break-inside: avoid;
    }
    .contrato-root .highlight { font-weight: bold; color: ${corDestaqueEscura}; }
    .contrato-root .signatures-container { margin-top: 40px; width: 100%; page-break-inside: avoid; break-inside: avoid; }
    .contrato-root .signature-table { width: 100%; border-collapse: collapse; }
    .contrato-root .signature-table td { width: 50%; padding: 0 20px; text-align: center; vertical-align: top; }
    .contrato-root .line { margin-top: 45px; border-top: 1px solid #4a5568; margin-bottom: 5px; }
    .contrato-root .footer-info { text-align: center; font-size: 8.5pt; color: #718096; margin-top: 35px; border-top: 1px solid #e2e8f0; padding-top: 10px; }
    .contrato-root h1, .contrato-root h2, .contrato-root .header { page-break-after: avoid; break-after: avoid; }
    @media print {
      .contrato-root, .contrato-root * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      .contrato-root .section-title {
        background-color: ${corPrimaria} !important;
        background-image: linear-gradient(${corPrimaria}, ${corPrimaria}) !important;
        color: #ffffff !important;
      }
      .contrato-root .parties-table td.label {
        background-color: #f7fafc !important;
        background-image: linear-gradient(#f7fafc, #f7fafc) !important;
      }
      .contrato-root .alert-box {
        background-color: #fffaf0 !important;
        background-image: linear-gradient(#fffaf0, #fffaf0) !important;
      }
    }
  </style>

  <div class="header">
    ${logoSrc ? `<img class="logo" src="${logoSrc}" alt="${empresaNome}">` : ''}
    <h1>Contrato de Projeto e Instalação Fotovoltaico</h1>
    <h2>Instrumento Particular de Prestação de Serviços</h2>
  </div>

  <div class="section">
    <div class="section-title">1. Partes Contratantes</div>
    <table class="parties-table">
      <tr>
        <td class="label">CONTRATADA</td>
        <td>
          <strong>${empresaRazao}</strong><br>
          CNPJ nº ${empresaCnpj}<br>
          Sede: ${empresaEndereco}
        </td>
      </tr>
      <tr>
        <td class="label">CONTRATANTE</td>
        <td>
          <strong>${clienteNome}</strong><br>
          CPF/CNPJ nº ${clienteCpf}
        </td>
      </tr>
    </table>
  </div>

  <div class="section">
    <div class="section-title">2. Objeto do Contrato</div>
    <p>O presente instrumento tem por objeto a prestação de serviços de instalação de sistema fotovoltaico, incluindo a homologação junto à concessionária de energia elétrica, monitoramento do sistema pelo período de 1 (um) ano, e o fornecimento de garantia dos equipamentos (módulos fotovoltaicos e inversor) em estrita conformidade com as políticas e prazos estabelecidos pelos respectivos fabricantes.</p>
    <div class="alert-box">
      <strong>2.1. ESPECIFICAÇÃO DO KIT SOLAR:</strong> ${kitSolarHtml}<br>
      <strong>2.2. VALOR DO PROJETO:</strong> ${valorTexto}
    </div>
  </div>

  <div class="section">
    <div class="section-title">3. Obrigações da Contratada</div>
    <ol>
      <li>Fornecimento e instalação de materiais elétricos estritamente necessários à execução do serviço e instalação do projeto (quadro de string DC, quadro de paralelo AC);</li>
      <li>Fornecimento e instalação de estrutura metálica para suporte de módulos fotovoltaicos;</li>
      <li>Conexão elétrica entre módulos, quadros DC, inversor e quadro de baixa tensão;</li>
      <li>Teste de funcionalidade e entrega de conformidade da usina;</li>
      <li>Avaliação do projeto de ajuste, caso seja necessário;</li>
      <li>Tratamento do processo de homologação do sistema junto à Concessionária de Energia Elétrica (Equatorial) e solicitação de aumento de carga (de monofásico para bifásico).</li>
    </ol>
  </div>

  <div class="section">
    <div class="section-title">4. Prazo de Entrega e Homologação</div>
    <p>O prazo global estabelecido para a conclusão da entrega do kit solar, instalação e a devida homologação do sistema fotovoltaico é de <span class="highlight">90 (noventa) dias</span>, contados a partir da assinatura deste instrumento.</p>
    <p><strong>Parágrafo Único:</strong> Excluem-se expressamente do cômputo deste prazo os períodos de atraso decorrentes única e exclusivamente dos trâmites, análises, vistorias e aprovações de responsabilidade da concessionária de energia elétrica local (Equatorial), bem como as hipóteses previstas na Cláusula 8 deste instrumento.</p>
  </div>

  <div class="section">
    <div class="section-title">5. Itens Excluídos do Contrato</div>
    <p>Estão expressamente excluídos do escopo de fornecimento e responsabilidade da CONTRATADA:</p>
    <ol>
      <li>Obras civis: intervenções na estrutura de edifícios, fundações, terraplenagem, armação, drenagem, ampliação, inovação ou restauração no imóvel;</li>
      <li>Fornecimento de telhas, exceto na hipótese de alguma unidade vir a ser danificada por culpa dos técnicos no momento exato da instalação;</li>
      <li>Obras de transmissão de energia em linha aérea: o cabeamento que liga a residência até a rede da concessionária, sendo tais procedimentos de responsabilidade exclusiva da concessionária de energia;</li>
      <li>Estrutura mecânica de reforço do telhado (mesmo onde previsto no laudo de vistoria);</li>
      <li>Implantação e custeio de poste auxiliar, quando necessário e indicado na vistoria técnica;</li>
      <li>Cálculo estrutural do telhado (quando não previsto especificamente no item 2.1).</li>
    </ol>
  </div>

  <div class="section">
    <div class="section-title">6. Responsabilidades da Contratante</div>
    <ul>
      <li>Manter o ponto de conexão de internet ligado ao sistema fotovoltaico sempre ativo, devendo realizar todos os procedimentos e medidas indicadas pela CONTRATADA para restabelecer o pleno funcionamento em caso de falha de conexão;</li>
      <li>Armazenar o material entregue em local seguro, sendo de sua total e exclusiva responsabilidade prevenir a ocorrência de roubo, furto, extravio e danos aos materiais após a entrega e durante a execução da instalação;</li>
      <li>Responsabilizar-se civil e integralmente por quaisquer ocorrências de roubo e furto após a entrega dos materiais no local e durante a prestação do serviço.</li>
    </ul>
  </div>

  <div class="section">
    <div class="section-title">7. Garantia e Manutenção</div>
    <p>A CONTRATADA garante a perfeita execução dos serviços e a integridade técnica da instalação realizada.</p>
    <p>A CONTRATADA concede garantia de <span class="highlight">1 (um) ano</span> sobre a instalação executada, contada a partir da data de conclusão e comissionamento do sistema fotovoltaico, cobrindo eventuais vícios ou defeitos decorrentes exclusivamente da execução dos serviços de instalação por ela realizados.</p>
    <p>A garantia legal e contratual dos equipamentos (placas/módulos e inversores) é de responsabilidade direta e exclusiva dos respectivos fabricantes.</p>
    <p>A ancoragem dos módulos (via Parafuso M10/HUCK) dar-se-á estritamente nas ripas de apoio das telhas. Não é de responsabilidade da CONTRATADA a intervenção, perfuração ou fixação em caibros (pernamancas), por se tratarem de componentes da estrutura vital do telhado, ficando a garantia da instalação restrita à metodologia padrão aqui descrita.</p>
  </div>

  <div class="section">
    <div class="section-title">8. Danos Cobertos</div>
    <p>Os danos causados aos equipamentos serão cobertos pela CONTRATADA única e exclusivamente caso sejam decorrentes de transporte inadequado sob sua responsabilidade, má instalação ou intercorrências provocadas diretamente durante o processo de instalação.</p>
    <p>Quaisquer danos posteriores à entrega e comissionamento do sistema são de responsabilidade integral do CONTRATANTE.</p>
  </div>

  <div class="section">
    <div class="section-title">9. Suspensão de Prazos</div>
    <p>Os prazos contratuais, incluindo o prazo global de 90 dias, serão suspensos sempre que ocorrerem atrasos decorrentes de condutas ou fatos não imputáveis à CONTRATADA, voltando a correr pelo tempo restante assim que for completamente corrigida ou cessada a circunstância impeditiva. São exemplos de causas de suspensão:</p>
    <ol type="a">
      <li>Estudo de rede realizado pela concessionária de energia elétrica;</li>
      <li>Necessidade de obras de ajuste de estrutura ou reformas no imóvel;</li>
      <li>Caso fortuito, força maior ou dificuldades supervenientes de acesso seguro ao imóvel;</li>
      <li>Interrupções no fornecimento local de água ou energia elétrica superiores a 2 (duas) horas por dia;</li>
      <li>Atrasos motivados por atos do CONTRATANTE, entes públicos ou da própria concessionária;</li>
      <li>Alteração ou modificação do projeto original por solicitação do CONTRATANTE;</li>
      <li>Recusa ou atraso na homologação por ausência do CONTRATANTE ou de responsável no imóvel;</li>
      <li>Existência de débitos financeiros em aberto do imóvel junto à concessionária de energia elétrica;</li>
      <li>Alteração ou substituição de Unidades Consumidoras que não tenham sido informadas por escrito no prazo adequado.</li>
    </ol>
  </div>

  <div class="section">
    <div class="section-title">10. Cancelamento</div>
    <p>Caso o CONTRATANTE opte por cancelar o presente contrato, em qualquer fase de sua execução, ficará obrigado a pagar à CONTRATADA uma multa correspondente a <span class="highlight">${multaTexto}</span>, a título de indenização pelos custos, despesas e prejuízos decorrentes do cancelamento.</p>
    <p><strong>Parágrafo Único:</strong> A multa de cancelamento é devida independentemente do estágio de avanço do projeto (projeto, aquisição de materiais, instalação ou homologação), e deverá ser quitada pelo CONTRATANTE no prazo de até 10 (dez) dias úteis contados da comunicação formal do cancelamento.</p>
  </div>

  <div class="section">
    <div class="section-title">11. Contato e Suporte</div>
    <p>Qualquer dúvida, solicitação de suporte técnico ou atendimento referente ao sistema fotovoltaico poderá ser formalizada através do telefone <strong>${empresaTelefone}</strong> ou pelo e-mail <strong>${empresaEmail}</strong>.</p>
  </div>

  <div class="section">
    <div class="section-title">12. Disposições Finais e Foro</div>
    <p>As partes elegem, de comum acordo, o foro da <strong>Comarca de Ananindeua - PA</strong> para dirimir quaisquer dúvidas, controvérsias ou litígios oriundos da interpretação ou execução deste contrato, com renúncia expressa a qualquer outro, por mais privilegiado que seja.</p>
    <p>E, por estarem assim justas e contratadas, as partes assinam o presente instrumento em duas vias de igual teor e forma.</p>
  </div>

  <div class="signatures-container">
    <table class="signature-table">
      <tr>
        <td>
          <div class="line"></div>
          <strong>${clienteNome}</strong><br>
          CONTRATANTE
        </td>
        <td>
          <div class="line"></div>
          <strong>${empresaNome.toUpperCase()}</strong><br>
          CONTRATADA
        </td>
      </tr>
    </table>
  </div>

  <div class="footer-info">
    <strong>${empresaNome}</strong> &bull; CNPJ: ${empresaCnpj}<br>
    ${empresaEndereco}<br>
    Telefone: ${empresaTelefone} | E-mail: ${empresaEmail}
  </div>
</div>`
}

/**
 * Documento HTML completo para download/edição (mesmo conteúdo do PDF).
 * Inclui regras de impressão: fundo dos tópicos e margem em todas as páginas.
 */
export function wrapContratoHtmlDocumento(html, titulo) {
  const tituloSafe = escapeHtml(titulo || 'Contrato')
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${tituloSafe}</title>
  <style>
    html, body { margin: 0; padding: 0; background: #ffffff; }
    body { max-width: 794px; margin: 24px auto; padding: 0 16px; }
    @page {
      size: A4;
      margin: 18mm 16mm;
    }
    @media print {
      html, body {
        max-width: none !important;
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        background: #ffffff !important;
      }
      html, body, body * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
    }
  </style>
</head>
<body contenteditable="true">
${html}
</body>
</html>`
}

export function nomeArquivoContrato(nomeCliente, extensao) {
  const nomeBase = (nomeCliente || 'cliente').trim().split(' ').join('_')
  return `Contrato_${nomeBase}_${new Date().toISOString().split('T')[0]}.${extensao}`
}
